/**
 * AWS Cognito error codes
 */
export enum CognitoErrorCode {
  NOT_AUTHORIZED = "NotAuthorizedException",
  USER_NOT_FOUND = "UserNotFoundException",
  USERNAME_EXISTS = "UsernameExistsException",
  CODE_MISMATCH = "CodeMismatchException",
  EXPIRED_CODE = "ExpiredCodeException",
  LIMIT_EXCEEDED = "LimitExceededException",
  INVALID_PARAMETER = "InvalidParameterException",
  INVALID_PASSWORD = "InvalidPasswordException",
  PRE_SIGNUP_ERROR = "PreSignUpException",
  USER_NOT_CONFIRMED = "UserNotConfirmedException",
  ALIAS_EXISTS = "AliasExistsException",
  TOO_MANY_REQUESTS = "TooManyRequestsException",
  INVALID_LAMBDA_RESPONSE = "InvalidLambdaResponseException",
  USER_LAMBDA_VALIDATION = "UserLambdaValidationException",
}

/**
 * User-friendly error messages for Cognito errors
 */
const ERROR_MESSAGES: Record<CognitoErrorCode, string> = {
  [CognitoErrorCode.NOT_AUTHORIZED]:
    "Incorrect email or password. Please try again.",
  [CognitoErrorCode.USER_NOT_FOUND]:
    "No account found with this email address.",
  [CognitoErrorCode.USERNAME_EXISTS]:
    "An account with this email already exists.",
  [CognitoErrorCode.CODE_MISMATCH]:
    "Invalid verification code. Please check and try again.",
  [CognitoErrorCode.EXPIRED_CODE]:
    "Verification code has expired. Please request a new one.",
  [CognitoErrorCode.LIMIT_EXCEEDED]:
    "Too many attempts. Please try again later.",
  [CognitoErrorCode.INVALID_PARAMETER]:
    "Invalid input provided. Please check your information.",
  [CognitoErrorCode.INVALID_PASSWORD]:
    "Password does not meet requirements. Must be at least 8 characters with uppercase, lowercase, and numbers.",
  [CognitoErrorCode.PRE_SIGNUP_ERROR]:
    "Unable to create account. This email domain may not be allowed.",
  [CognitoErrorCode.USER_NOT_CONFIRMED]:
    "Account not verified. Please check your email for verification code.",
  [CognitoErrorCode.ALIAS_EXISTS]:
    "An account with this email or phone number already exists.",
  [CognitoErrorCode.TOO_MANY_REQUESTS]:
    "Too many requests. Please wait a moment and try again.",
  [CognitoErrorCode.INVALID_LAMBDA_RESPONSE]:
    "Authentication service error. Please try again later.",
  [CognitoErrorCode.USER_LAMBDA_VALIDATION]:
    "Unable to complete authentication. Please contact support if this persists.",
}

/**
 * Map Cognito error to user-friendly message
 * @param error - Error from AWS Cognito
 * @returns User-friendly error message
 */
export const mapCognitoError = (error: Error | unknown): string => {
  if (!error) {
    return "An unexpected error occurred. Please try again."
  }

  // Handle Error objects
  if (error instanceof Error) {
    const errorName = error.name as CognitoErrorCode

    // Check if it's a known Cognito error
    if (Object.values(CognitoErrorCode).includes(errorName)) {
      return ERROR_MESSAGES[errorName]
    }

    // Return the error message if it's a custom message
    if (error.message) {
      return error.message
    }
  }

  // Handle string errors
  if (typeof error === "string") {
    return error
  }

  // Fallback
  return "An unexpected error occurred. Please try again."
}

/**
 * Check if error is a specific Cognito error code
 */
export const isCognitoError = (
  error: Error | unknown,
  code: CognitoErrorCode
): boolean => {
  if (error instanceof Error) {
    return error.name === code
  }
  return false
}
